<template>
    <BaseDialog v-model:open="isOpen" title="Draft review for Workday" show-close float-close dialog-content-class="md:w-[560px] p-6">
        <div v-if="draft" class="flex flex-col gap-5">
            <div class="flex flex-col gap-1">
                <h3 class="title-section text-valence-grey-800">{{ revieweeName || "Your report" }}'s review</h3>
                <p class="body-small text-valence-grey-600">Drafted from your conversation. Review or edit each comment, then submit it to Workday.</p>
            </div>

            <template v-if="!submitted">
                <section v-for="section in localDraft.sections" :key="section.section_id" class="flex flex-col gap-2">
                    <h4 class="body-semibold text-valence-grey-800">{{ section.label }}</h4>

                    <div class="flex flex-col gap-1">
                        <div class="body-small text-valence-grey-600">Overall {{ section.label }} comment</div>
                        <BaseTextarea
                            v-model:input="section.comment"
                            rows="3"
                            :disabled="loading"
                            :aria-label="`Overall ${section.label} comment`"
                            :placeholder="`Add an overall comment for ${section.label}.`"
                            class="caret-valence-primary-500 leading-[normal]" />
                    </div>

                    <div v-for="item in section.items" :key="item.item_id" class="flex flex-col gap-1 pl-3 border-l-2 border-valence-grey-200">
                        <div class="body-small text-valence-grey-600">{{ item.label }}</div>
                        <BaseTextarea
                            v-model:input="item.comment"
                            rows="2"
                            :disabled="loading"
                            :aria-label="item.label"
                            :placeholder="`Add a comment on ${item.label}.`"
                            class="caret-valence-primary-500 leading-[normal]" />
                    </div>
                </section>

                <section class="flex flex-col gap-1">
                    <h4 class="body-semibold text-valence-grey-800">Overall review comment</h4>
                    <BaseTextarea
                        v-model:input="localDraft.overall_comment"
                        rows="3"
                        :disabled="loading"
                        aria-label="Overall review comment"
                        placeholder="Add an overall comment for the review."
                        class="caret-valence-primary-500 leading-[normal]" />
                </section>

                <div v-if="noticeMessage" class="body-small text-red-700" role="alert">{{ noticeMessage }}</div>

                <div class="flex justify-end gap-2">
                    <BaseButton theme="tertiary" :disabled="loading" @click="isOpen = false">Cancel</BaseButton>
                    <BaseButton theme="primary" data-testid="submit-manager-review-dialog" :disabled="loading" class="flex items-center justify-center gap-1" @click="handleSubmit">
                        <LoadingSpinner v-if="loading" class="h-4" />Submit to Workday
                    </BaseButton>
                </div>
            </template>

            <div v-else class="flex flex-col items-center gap-3 py-4 text-center" data-testid="manager-review-submitted">
                <i class="bi bi-check-circle text-2xl text-valence-primary-500" aria-hidden="true"></i>
                <p class="nadia-chat text-valence-grey-800">Submitted to {{ revieweeName || "your report" }}'s Workday review.</p>
                <BaseButton theme="primary" @click="isOpen = false">Done</BaseButton>
            </div>
        </div>
    </BaseDialog>
</template>

<script setup lang="ts">
import { ChatAction } from "~vue/chatActions";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import { useActionHandler } from "~vue/composables/useActionHandler";
import { useDeferredLoading } from "~vue/composables/useDeferredLoading";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { PERFORMANCE_REVIEW_EVENT_PREFIX } from "~vue/Moments/constants";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { inject, reactive, ref, watch } from "vue";

interface CardItem {
    item_id: string;
    label: string;
    comment: string;
}
interface CardSection {
    section_id: string;
    label: string;
    comment: string;
    items: CardItem[];
}
interface CardDraft {
    review_ref: string;
    overall_comment: string;
    sections: CardSection[];
}

interface Props {
    draft: CardDraft | null;
    revieweeName?: string | null;
    messageId: number;
}

const props = defineProps<Props>();
const isOpen = defineModel<boolean>("open", { default: false });

const { execute } = useActionHandler();
const coachingSessionUuid = inject<string | undefined>("coachingSessionUuid", undefined);
const { loading, start: startLoading, stop: stopLoading } = useDeferredLoading();

const submitted = ref(false);
const noticeMessage = ref("");

function cloneDraft(source: CardDraft | null): CardDraft {
    if (!source) {
        return { review_ref: "", overall_comment: "", sections: [] };
    }
    return JSON.parse(JSON.stringify(source));
}

const localDraft = reactive<CardDraft>(cloneDraft(props.draft));

// Reset the editable copy and state whenever a fresh draft is opened.
watch(
    () => [props.draft, isOpen.value] as const,
    ([draft, open]) => {
        if (open && draft) {
            const cloned = cloneDraft(draft);
            localDraft.review_ref = cloned.review_ref;
            localDraft.overall_comment = cloned.overall_comment;
            localDraft.sections = cloned.sections;
            submitted.value = false;
            noticeMessage.value = "";
        }
    },
);

async function handleSubmit() {
    startLoading();
    noticeMessage.value = "";
    logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}submit_manager_review_clicked`, { session_id: coachingSessionUuid }, coachingSessionUuid);

    try {
        const result = await execute(ChatAction.SUBMIT_MANAGER_REVIEW_DRAFT, { message_id: props.messageId, draft: JSON.parse(JSON.stringify(localDraft)) }, { sync: true });
        if (result?.success) {
            submitted.value = true;
        } else {
            noticeMessage.value = result?.payload?.error ?? "We couldn't submit this review to Workday. Please try again later.";
        }
    } catch (e) {
        logError(e);
        noticeMessage.value = "We couldn't submit this review to Workday. Please try again later.";
    } finally {
        stopLoading();
    }
}
</script>
